/**
 * BuriedVerse — overlay STRATEGY PROOFS (2026-07-22).
 *
 * Architecture is inherited from BibleDecoderOverlayFinal.tsx (2 slots + CTA + logo).
 * The SKIN is deliberately different: the sibling is cool Inter/IBM-Plex-Mono + mint over
 * PHOTOREAL plates. BuriedVerse renders style-C storybook-painterly DIORAMA (warm, brushy),
 * so a cool mono HUD reads as a foreign object pasted onto a painting.
 *
 * Two divergences from the sibling, both deliberate:
 *   1. Warm aged-ink palette + serif for the word itself (parchment/gold, not mint/mono).
 *   2. Soft SCRIM GRADIENT instead of the sibling's hard 3-layer drop-shadow stack. A black
 *      glow halo survives a bright photo sky but sits badly on brushwork; a painted wash does not.
 *
 * Slot 2 ("BigMoment" in the sibling) is specialised here into the HERO card, because on this
 * channel the reveal IS a word. It rotates 3 ways: word-decode / myth-bust / quote.
 * Slot 1 (FactTag) rotates 3 ways: citation / place / author.
 *
 * Plates are REAL E01 (25221141) anchors, matched to the clip whose narration they sit under.
 */
import { AbsoluteFill, Img, staticFile, useCurrentFrame, interpolate, Easing } from "remotion";
import { loadFont as loadSerif } from "@remotion/google-fonts/EBGaramond";
import { loadFont as loadSans } from "@remotion/google-fonts/Lato";

const { fontFamily: serif } = loadSerif("normal", {
  weights: ["400", "500", "600"],
  subsets: ["latin", "greek"],
});
const { fontFamily: sans } = loadSans("normal", { weights: ["400", "700"], subsets: ["latin"] });

// ── warm aged-ink palette (vs the sibling's cool INK/#8fd6c4 mint) ──
const PARCHMENT = "#f7f0e3";
const PARCHMENT_DIM = "#d3c4a9";
const GOLD = "#d9a441";
// kept soft + short-range: enough to lift text off a busy mid-frame diorama,
// not the hard halo the photoreal sibling needs against a bright sky.
const SOFT = "0 1px 3px rgba(20,12,6,0.85), 0 2px 14px rgba(20,12,6,0.45)";

const useIn = (delay: number, dur = 14) => {
  const frame = useCurrentFrame();
  return interpolate(frame, [delay, delay + dur], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
    easing: Easing.out(Easing.cubic),
  });
};

// ═══ SLOT 1 — TOP-LEFT FACT TAG (rotates: citation / place / author) ═══
export const BvFactTag: React.FC<{ kicker: string; value: string; startFrame?: number }> = ({
  kicker,
  value,
  startFrame = 0,
}) => {
  const frame = useCurrentFrame() - startFrame;
  const o = interpolate(frame, [3, 16], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const y = interpolate(frame, [3, 18], [-7, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
    easing: Easing.out(Easing.cubic),
  });
  if (frame < 0) return null;
  return (
    <>
      {/* Corner scrim — a wash, not a panel.
          ⚠️ Was a 520x190 box with `linear-gradient(135deg, … 0 78%)`. A 135° linear gradient
          varies ONLY along its diagonal axis, so pixels near the bottom-left / top-right corners
          were still at ~0.2-0.3 alpha when the box ended — the div cut them off dead and that
          hard step read as a straight black line along the bottom and right edges.
          Fix: a RADIAL gradient anchored at the corner (fades toward bottom AND right equally),
          reaching alpha 0 at 100% of its own extent, inside a box LARGER than that extent —
          so every edge of the div is already fully transparent. Same principle that makes the
          bottom hero scrim clean: the fade must finish before the geometry does. */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: 700,
          height: 340,
          opacity: o,
          background:
            "radial-gradient(ellipse 620px 300px at 0% 0%, rgba(22,14,7,0.80) 0%, rgba(22,14,7,0.50) 34%, rgba(22,14,7,0.20) 62%, rgba(22,14,7,0.05) 84%, rgba(22,14,7,0) 100%)",
          pointerEvents: "none",
        }}
      />
      <div style={{ position: "absolute", top: 42, left: 46, opacity: o, transform: `translateY(${y}px)` }}>
        <div
          style={{
            fontFamily: sans,
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 3.4,
            color: GOLD,
            textTransform: "uppercase",
            textShadow: SOFT,
          }}
        >
          {kicker}
        </div>
        <div
          style={{
            fontFamily: serif,
            fontSize: 27,
            fontWeight: 500,
            color: PARCHMENT,
            marginTop: 3,
            textShadow: SOFT,
          }}
        >
          {value}
        </div>
      </div>
    </>
  );
};

// ═══ SLOT 2 — HERO CARD (rotates: word-decode / myth-bust / quote) ═══
export const BvHero: React.FC<{
  variant: "word" | "myth" | "quote";
  kicker: string;
  word?: string;
  translit?: string;
  gloss?: string;
  headline?: string;
  attribution?: string;
  startFrame?: number;
}> = ({ variant, kicker, word, translit, gloss, headline, attribution, startFrame = 0 }) => {
  const frame = useCurrentFrame() - startFrame;
  const o = useIn(startFrame + 4, 16);
  const rule = interpolate(frame, [8, 30], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
    easing: Easing.out(Easing.cubic),
  });
  const y = interpolate(frame, [4, 22], [10, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
    easing: Easing.out(Easing.cubic),
  });
  if (frame < 0) return null;
  return (
    <>
      {/* bottom scrim — painted wash so text reads over brushwork */}
      <div
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          height: 300,
          opacity: o,
          background:
            "linear-gradient(to top, rgba(20,12,6,0.90) 0%, rgba(20,12,6,0.66) 38%, rgba(20,12,6,0.22) 72%, rgba(20,12,6,0) 100%)",
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "absolute",
          left: 60,
          right: 60,
          bottom: 52,
          opacity: o,
          transform: `translateY(${y}px)`,
        }}
      >
        <div
          style={{
            fontFamily: sans,
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 3.6,
            color: GOLD,
            textTransform: "uppercase",
            textShadow: SOFT,
          }}
        >
          {kicker}
        </div>
        <div
          style={{
            height: 1.5,
            width: `${rule * 100}%`,
            maxWidth: 148,
            background: GOLD,
            opacity: 0.85,
            marginTop: 7,
            marginBottom: 13,
          }}
        />

        {variant === "word" && (
          <>
            <div
              style={{
                fontFamily: serif,
                fontSize: 66,
                fontWeight: 500,
                lineHeight: 1.02,
                color: PARCHMENT,
                textShadow: SOFT,
              }}
            >
              {word}
            </div>
            <div
              style={{
                fontFamily: serif,
                fontSize: 26,
                fontStyle: "italic",
                color: PARCHMENT_DIM,
                marginTop: 6,
                textShadow: SOFT,
              }}
            >
              {translit}
            </div>
            <div
              style={{
                fontFamily: sans,
                fontSize: 19,
                color: PARCHMENT,
                marginTop: 9,
                letterSpacing: 0.2,
                textShadow: SOFT,
              }}
            >
              {gloss}
            </div>
          </>
        )}

        {variant === "myth" && (
          <div
            style={{
              fontFamily: serif,
              fontSize: 44,
              fontWeight: 500,
              lineHeight: 1.16,
              color: PARCHMENT,
              maxWidth: 900,
              textShadow: SOFT,
            }}
          >
            {headline}
          </div>
        )}

        {variant === "quote" && (
          <>
            <div
              style={{
                fontFamily: serif,
                fontSize: 46,
                fontStyle: "italic",
                fontWeight: 400,
                lineHeight: 1.18,
                color: PARCHMENT,
                maxWidth: 900,
                textShadow: SOFT,
              }}
            >
              {headline}
            </div>
            <div
              style={{
                fontFamily: sans,
                fontSize: 15,
                fontWeight: 700,
                letterSpacing: 2.2,
                color: GOLD,
                marginTop: 12,
                textTransform: "uppercase",
                textShadow: SOFT,
              }}
            >
              {attribution}
            </div>
          </>
        )}
      </div>
    </>
  );
};

/**
 * REAL LOGO — the "diorama" mark, finalised by Yash 2026-07-22.
 * 70px wide on the 1280x704 base, top:20 right:20 = Jyotirlinga-family convention
 * (same as bible_decoder's locked spec + the ffmpeg "overlay=W-w-20:20" composite rule).
 * Width fixed, height auto so the real aspect is preserved — never force a square box.
 *
 * ⚠️ Source `buried_verse_diorama.png` on R2 is PNG colour-type 2 (RGB, NO alpha, no tRNS) on a
 * white ground — it would composite as a white square. This uses the transparent version made by
 * corner flood-fill knockout + bbox crop (983x1051 -> 70x75px). Swap in a true alpha export when
 * one exists; if it reuses the same R2 key, bust the CDN with ?v=N.
 */
const BvLogo: React.FC = () => {
  const frame = useCurrentFrame();
  const fadeIn = interpolate(frame, [0, 36], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return (
    <Img
      src={staticFile("buried_verse_diorama_ko.png")}
      style={{ position: "absolute", top: 20, right: 20, width: 70, height: "auto", opacity: fadeIn * 0.85 }}
    />
  );
};

const Plate: React.FC<{ src: string; children: React.ReactNode }> = ({ src, children }) => (
  <AbsoluteFill style={{ background: "#000" }}>
    <Img src={staticFile(src)} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
    <BvLogo />
    {children}
  </AbsoluteFill>
);

// ── 1. HERO / word-decode. Plate sits under "The Greek is merimnaō." (seq 60100, 75.0s) ──
export const BV_Demo1_Word: React.FC = () => (
  <Plate src="bv_demo/p1_merimnao.jpg">
    <BvHero
      variant="word"
      kicker="The word"
      word="μεριμνάω"
      translit="merimnaō"
      gloss="To be anxious, to be weighed down by care."
    />
  </Plate>
);

// ── 2. FACT TAG / citation. Under "...like a wave of the sea" (seq 190300, 267.4s) ──
export const BV_Demo2_Citation: React.FC = () => (
  <Plate src="bv_demo/p2_wave.jpg">
    <BvFactTag kicker="Citation" value="James 1:6" />
  </Plate>
);

// ── 3. FACT TAG / place, on a DARK plate. Under the Gethsemane beat (seq 790300, 1061.6s) ──
export const BV_Demo3_Place: React.FC = () => (
  <Plate src="bv_demo/p3_gethsemane.jpg">
    <BvFactTag kicker="Place" value="Gethsemane, Mount of Olives" />
  </Plate>
);

// ── 4. HERO / myth-bust. Headline is the clip's OWN narration line (seq 90200, 121.9s) ──
export const BV_Demo4_Myth: React.FC = () => (
  <Plate src="bv_demo/p4_mythbust.jpg">
    <BvHero variant="myth" kicker="The myth" headline={"Merimnaō does not mean “to be divided.”"} />
  </Plate>
);

// ── 5. BOTH SLOTS FIRING — the reveal beat, max density. Psalm 77 (seq 740000, 989.8s) ──
export const BV_Demo5_Both: React.FC = () => (
  <Plate src="bv_demo/p5_psalm77.jpg">
    <BvFactTag kicker="Author" value="Asaph" />
    <BvHero
      variant="quote"
      kicker="The text"
      headline={"“Will the Lord reject forever?”"}
      attribution="Psalm 77:7"
    />
  </Plate>
);

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
